<template>
    <div class="pb-10">
        <v-row v-if="variants?.length" dense class="mb-4">
            <v-col cols="12" md="2" />
            <v-col cols="12" md="4">
                <v-autocomplete
                    ref="option"
                    v-model="contract.variantId"
                    :items="variants"
                    item-title="title"
                    item-value="value"
                    closable-chips
                    hide-details
                    clearable
                    :label="$t('product_variant')"
                    density="compact"
                    color="primary"
                    data-cy="airline-code"
                />
            </v-col>
        </v-row>
        <v-row dense class="mb-4">
            <v-col cols="12" md="2" />
            <v-col cols="12" md="4">
                <v-autocomplete
                    ref="option"
                    v-model="contract.eventType"
                    :items="useEvent"
                    item-title="title"
                    item-value="value"
                    closable-chips
                    hide-details
                    :label="$t('event_type')"
                    density="compact"
                    color="primary"
                    data-cy="variant"
                    @update:model-value="updateVenues()"
                />
            </v-col>
        </v-row>
        <v-row dense class="mb-6">
            <v-col cols="12" md="2" />
            <v-col cols="12" sm="3" md="2" class="pb-4">
                <v-radio-group v-model="contract.sharingType" :disabled="editMode" density="compact" hide-details>
                    <v-radio
                        v-for="{value, title} in useSharing"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('location') }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="contract.countryId"
                            :items="useCountries"
                            item-title="title"
                            item-value="value"
                            :label="`${$t('country')}*`"
                            closable-chips
                            clearable
                            density="compact"
                            color="primary"
                            data-cy="country-id"
                            :rules="[requiredRule('country')]"
                            @update:model-value="updateCities()"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            ref="cityRef"
                            v-model="contract.cityId"
                            :items="cities"
                            item-title="title"
                            item-value="value"
                            :label="`${$t('city')}*`"
                            :loading="isCitiesLoading"
                            :disabled="isCitiesLoading || !cities.length"
                            closable-chips
                            clearable
                            density="compact"
                            color="primary"
                            data-cy="city-id"
                            :rules="[requiredRule('city')]"
                            @update:model-value="updateVenues()"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-autocomplete
                            ref="option"
                            v-model="contract.eventVenueId"
                            :items="venues"
                            item-title="name"
                            item-value="id"
                            closable-chips
                            hide-details
                            clearable
                            :label="$t('venue')"
                            density="compact"
                            color="primary"
                            data-cy="venue"
                            @update:model-value="updateSelectedVenue"
                        />
                        <div v-if="contract.eventVenueId" class="text-subtitle-2 pt-1">
                            {{
                                `${selectedVenue?.street || ''}${selectedVenue?.street && selectedVenue?.postalCode ? ', ' : ''}${selectedVenue?.postalCode || ''}`
                            }}
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>

<script setup lang="ts">
    import {contractServiceType, type SelectOptionType, Sharing} from '~/utils/types/types'
    import {useCountries, useEvent} from '~/composables/form-helpers'
    import {requiredRule} from '~/composables/rules'

    const props = defineProps<{
        editMode?: boolean
    }>()

    const contract = defineModel<any>()
    const isCitiesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()
    const venues = ref([])
    const selectedVenue = ref(null)
    const variants = ref<SelectOptionType[]>([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadVariants()
        await loadVenues()
        if (contract.value.cityId) await loadCities()
        if (contract.value.eventVenueId) {
            selectedVenue.value = venues.value.find(({id}) => id === contract.value.eventVenueId)
        }
    })

    watch(useCountries, async () => {
        if (contract.value.countryId) await loadCities()
    })

    async function loadVariants() {
        try {
            const productType = contractServiceType.concert
            const {productVariants} = await app.$api.productVariants.get({
                productType,
            })
            variants.value = productVariants.map(({id, name}) => ({value: id, title: name}))
        } catch (e) {
            variants.value = []
        }
    }

    const updateVenues = () => {
        selectedVenue.value = null
        contract.value.eventVenueId = null
        loadVenues()
    }

    const updateSelectedVenue = async () => {
        if (contract.value.eventVenueId) {
            selectedVenue.value = venues.value.find(({id}) => id === contract.value.eventVenueId)
            if (
                contract.value.countryId !== selectedVenue.value.countryId ||
                contract.value.cityId !== selectedVenue.value.cityId
            ) {
                contract.value.countryId = selectedVenue.value.countryId
                await loadCities()
                contract.value.cityId = selectedVenue.value.cityId
            }
        } else selectedVenue.value = null
    }

    async function loadVenues() {
        try {
            const rq = {
                ...(contract.value.cityId && {cityId: contract.value.cityId}),
                ...(contract.value.eventType && {eventType: contract.value.eventType}),
            }
            const {eventVenues} = await app.$api.venues.get(rq)
            venues.value = eventVenues.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            venues.value = []
        }
    }

    const updateCities = async (): Promise<void> => {
        cities.value = []
        updateVenues()
        if (contract.value.cityId) {
            contract.value.cityId = null
            cityRef.value.reset()
        }
        if (contract.value.countryId) await loadCities()
    }

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: contract.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => ({title: name, value: id}))
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const useSharing = computed(() => {
        const sharingTypes: Sharing[] = [Sharing.Shared, Sharing.Private]
        return sharingTypes.map(value => ({value, title: t(`sharingType.${value}`)}))
    })
</script>
